import { create } from 'zustand';
import { Race, UserRaces } from '../types/race';
import { fetchActiveRaces, fetchOngoingRaces, fetchUserRaces } from '../services/raceService';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

interface RaceStore {
  // State
  activeRaces: { races: Race[]; isLoading: boolean; error: string | null; lastUpdated: number | null };
  ongoingRaces: { races: Race[]; isLoading: boolean; error: string | null; lastUpdated: number | null };
  userRaces: { races: UserRaces | null; isLoading: boolean; error: string | null; lastUpdated: number | null };

  // Actions
  fetchActiveRaces: (forceUpdate?: boolean) => Promise<void>;
  fetchOngoingRaces: (forceUpdate?: boolean) => Promise<void>;
  fetchUserRaces: (address: string, forceUpdate?: boolean) => Promise<void>;
}

const useRaceStore = create<RaceStore>(set => ({
  // Initial state
  activeRaces: { races: [], isLoading: false, error: null, lastUpdated: null },
  ongoingRaces: { races: [], isLoading: false, error: null, lastUpdated: null },
  userRaces: { races: null, isLoading: false, error: null, lastUpdated: null },
  // Actions

  fetchActiveRaces: async (forceUpdate = false) => {
    const { activeRaces } = useRaceStore.getState();
    const now = Date.now();
    const isCacheValid =
      !!activeRaces.lastUpdated && now - activeRaces.lastUpdated < CACHE_DURATION && activeRaces.races.length > 0;

    // Return cached data if valid and not forcing update
    if (isCacheValid && !forceUpdate) {
      return;
    }

    try {
      set({ activeRaces: { ...activeRaces, isLoading: true, error: null } });
      const races = await fetchActiveRaces();
      set({
        activeRaces: { races, isLoading: false, error: null, lastUpdated: now },
      });
    } catch (error) {
      set({
        activeRaces: {
          ...activeRaces,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch active races',
        },
      });
    }
  },

  fetchOngoingRaces: async (forceUpdate = false) => {
    const { ongoingRaces } = useRaceStore.getState();
    const now = Date.now();
    const isCacheValid =
      !!ongoingRaces.lastUpdated && now - ongoingRaces.lastUpdated < CACHE_DURATION && ongoingRaces.races.length > 0;

    // Return cached data if valid and not forcing update
    if (isCacheValid && !forceUpdate) {
      return;
    }

    try {
      set({ ongoingRaces: { ...ongoingRaces, isLoading: true, error: null } });
      const races = await fetchOngoingRaces();
      set({
        ongoingRaces: { races, isLoading: false, error: null, lastUpdated: now },
      });
    } catch (error) {
      set({
        ongoingRaces: {
          ...ongoingRaces,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch ongoing races',
        },
      });
    }
  },

  fetchUserRaces: async (address: string, forceUpdate = false) => {
    const { userRaces } = useRaceStore.getState();
    const now = Date.now();
    const isCacheValid =
      !!userRaces.lastUpdated && now - userRaces.lastUpdated < CACHE_DURATION && userRaces.races !== null;

    // Return cached data if valid and not forcing update
    if (isCacheValid && !forceUpdate) {
      return;
    }

    try {
      set({ userRaces: { ...userRaces, isLoading: true, error: null } });
      const races = await fetchUserRaces(address);
      set({
        userRaces: { races, isLoading: false, error: null, lastUpdated: now },
      });
    } catch (error) {
      set({
        userRaces: {
          ...userRaces,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch user races',
        },
      });
    }
  },
}));

export default useRaceStore;
